import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { BSABrandStyles } from "@/components/BSABrandStyles";
import { ArrowRight, Mail, Receipt, MessageSquare, Star, CreditCard } from "lucide-react";

const BOOK_HREF = "/book?interest=After-Sale%20System";

const AFTER_JOB = [
  { icon: Receipt, title: "Invoice" },
  { icon: MessageSquare, title: "Survey" },
  { icon: Star, title: "Google review" },
  { icon: CreditCard, title: "POS stays connected" },
];

const EXAMPLES = ["Restaurants", "Trades", "Shops", "Clinics"];

export default function YardsPage() {
  return (
    <div className="min-h-screen bg-[#F5F2EC] text-[#0A1628]">
      <SEO
        title="After-Sale System"
        description="Every paid job invoices, surveys, and asks for the Google review. Nobody has to remember. Works after any payment. POS stays connected."
        keywords="after-sale system, invoice after job, Google review, customer survey, POS"
        url="https://bettersystems.ai/yards"
      />
      <BSABrandStyles />

      <section className="relative blueprint-grid grain overflow-hidden px-4 pt-12 pb-16 md:pt-20 md:pb-24">
        <div className="container relative z-10 mx-auto max-w-7xl">
          <h1 className="font-display mb-6 max-w-5xl text-5xl leading-[0.92] md:text-7xl lg:text-[88px]">
            After-Sale System
          </h1>
          <p className="font-body mb-4 max-w-3xl text-lg leading-relaxed text-[#0A1628]/80 md:text-2xl">
            The After-Sale System: every paid job invoices, surveys, and asks for the Google review, without anyone remembering. For any business that takes a payment.
          </p>
          <p className="font-body mb-8 max-w-2xl text-base leading-relaxed text-[#0A1628]/70 md:text-lg">
            Every paid job invoices, surveys, and asks for the Google review. Nobody has to remember.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button asChild size="lg" className="cta-orange h-14 px-8 text-base">
              <Link href={BOOK_HREF}>
                Book a 15-minute call <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="h-14 border-[#0A1628]/15 bg-white px-8 font-heading text-sm font-extrabold uppercase tracking-wide text-[#0A1628] hover:border-[#F26B1F] hover:text-[#F26B1F]">
              <Link href="/ai-sales">Free Sales Leak Scorecard</Link>
            </Button>
            <a
              href="mailto:rodolfo@bettersystems.ai"
              className="inline-flex h-14 items-center justify-center border border-[#0A1628]/15 bg-white px-8 font-heading text-sm font-extrabold uppercase tracking-wide text-[#0A1628] transition hover:border-[#F26B1F] hover:text-[#F26B1F]"
            >
              <Mail className="mr-2 h-5 w-5" />
              rodolfo@bettersystems.ai
            </a>
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-16 md:py-24">
        <div className="container mx-auto max-w-7xl">
          <h2 className="font-display mb-10 max-w-4xl text-4xl leading-[0.95] md:text-5xl">
            Invoice after every job
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {AFTER_JOB.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="border border-[#0A1628]/10 bg-[#F5F2EC] p-6">
                  <div className="mb-4 flex h-11 w-11 items-center justify-center bg-[#0A1628] text-white">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-heading text-xl font-black text-[#0A1628]">{item.title}</h3>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#E5E1D8] px-4 py-16 md:py-20">
        <div className="container mx-auto max-w-7xl">
          <h2 className="font-display mb-4 max-w-4xl text-4xl leading-[0.95] md:text-5xl">
            Survey and review, auto
          </h2>
          <p className="font-body max-w-2xl text-lg leading-relaxed text-[#0A1628]/75">
            Works after any payment. Restaurants, trades, shops, clinics. POS stays connected.
          </p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {EXAMPLES.map((label) => (
              <div
                key={label}
                className="border border-[#0A1628]/10 bg-white px-5 py-4 font-heading text-sm font-extrabold uppercase tracking-wide text-[#0A1628]"
              >
                {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="blueprint-grid-dark grain bg-[#0A1628] px-4 py-20 text-white md:py-28">
        <div className="container relative z-10 mx-auto max-w-5xl text-center">
          <h2 className="font-display mb-6 text-4xl leading-[0.95] md:text-6xl">
            After-Sale System
          </h2>
          <p className="font-body mx-auto mb-8 max-w-2xl text-lg text-white/75">
            Every paid job invoices, surveys, and asks for the Google review. Nobody has to remember.
          </p>
          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            <Button asChild size="lg" className="cta-orange h-14 px-10 text-base">
              <Link href={BOOK_HREF}>
                Book a 15-minute call <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <a
              href="mailto:rodolfo@bettersystems.ai"
              className="inline-flex h-14 items-center justify-center border border-white/20 bg-white/5 px-8 font-heading text-sm font-extrabold uppercase tracking-wide text-white transition hover:bg-white hover:text-[#0A1628]"
            >
              <Mail className="mr-2 h-5 w-5" />
              rodolfo@bettersystems.ai
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
